import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import GuidedTour from "@/components/GuidedTour";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Users,
  MessageSquare,
  Phone,
  CalendarCheck,
  TrendingUp,
  UploadCloud,
  Zap,
  Sparkles,
  PlayCircle,
  Printer,
  Send,
  AlertTriangle,
  Droplet,
  Package,
  UserMinus,
  UserX,
  ShieldAlert,
  Star,
  ArrowRight,
  Building2,
  Wand2,
  HelpCircle,
} from "lucide-react";

const STATUS_STYLES = {
  declined: "bg-stone-500 text-white",
  sent_30: "bg-blue-500 text-white",
  stage_30: "bg-blue-500 text-white",   // legacy alias — some rows saved with stage_30 as status
  sent_40: "bg-orange-500 text-white",
  stage_40: "bg-orange-500 text-white", // legacy alias
  scheduled: "bg-emerald-600 text-white",
  called_live: "bg-cyan-600 text-white",
  no_response: "bg-red-500 text-white",
};

const STATUS_LABELS = (s) => {
  const d1 = s?.stage_1_discount ?? 30;
  const d2 = s?.stage_2_discount ?? 40;
  return {
    declined: "Declined",
    sent_30: `1st Offer · ${d1}%`,
    stage_30: `1st Offer · ${d1}%`,
    sent_40: `2nd Offer · ${d2}%`,
    stage_40: `2nd Offer · ${d2}%`,
    scheduled: "Scheduled",
    called_live: "Called Live",
    no_response: "No Response",
  };
};

const BODY_SHOP_STATUS_META = {
  awaiting: { label: "Awaiting", cls: "bg-stone-400 text-white hover:bg-stone-500", next: "showed_up" },
  showed_up: { label: "Showed Up", cls: "bg-emerald-600 text-white hover:bg-emerald-700", next: "no_show" },
  no_show: { label: "No Show", cls: "bg-red-500 text-white hover:bg-red-600", next: "awaiting" },
};

const BODY_SHOP_KEYWORDS = [
  "bumper", "fender", "paint", "windshield", "hood",
  "dent", "collision", "body shop", "quarter panel", "door skin",
];

function isBodyShop(serviceItems = []) {
  const blob = serviceItems.map((s) => (s?.name || "").toLowerCase()).join(" ");
  return BODY_SHOP_KEYWORDS.some((kw) => blob.includes(kw));
}

function KpiCard({ label, value, sub, icon: Icon, accent, testid }) {
  // Solid colored header strip on top with BLACK label text (matches the solid
  // "15% Sent" / "25% Sent" pills — but keeps the header readable at a glance)
  return (
    <Card className="border-border relative overflow-hidden p-0" data-testid={testid}>
      <div className={`flex items-center justify-between px-3 py-2 ${accent}`}>
        <div
          className="font-semibold uppercase leading-tight tracking-wider text-neutral-900"
          style={{ fontSize: "0.68rem" }}
        >
          {label}
        </div>
        <Icon className="w-3.5 h-3.5 text-neutral-900" strokeWidth={2.5} />
      </div>
      <div className="px-3 py-3">
        <div className="font-display text-2xl lg:text-3xl font-bold tracking-tighter tabular-nums break-words text-navy">
          {value}
        </div>
        {sub && <div className="text-[0.68rem] text-muted-foreground mt-1.5 leading-snug">{sub}</div>}
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [optOuts, setOptOuts] = useState({ total: 0, recent: [] });
  const [consentStats, setConsentStats] = useState({ no_consent: 0, consented: 0, total_unique_customers: 0 });
  const [todayPulse, setTodayPulse] = useState(null);
  const [settings, setSettings] = useState(null);
  const [byAdvisor, setByAdvisor] = useState([]);
  const [declines, setDeclines] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [bodyShopEnabled, setBodyShopEnabled] = useState(false);
  const [apptByDecline, setApptByDecline] = useState({});
  const [advisorFilter, setAdvisorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pipelineWindow, setPipelineWindow] = useState("all");
  const [busy, setBusy] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [demoMode, setDemoMode] = useState(true);
  const [testForm, setTestForm] = useState({
    name: "",
    phone: "",
    vehicle: "2022 Ford F-150",
    service: "Front Brake Pads & Rotor Resurface",
    discount: "30",
  });
  const [personalizeName, setPersonalizeName] = useState("");
  const [personalizeGlow, setPersonalizeGlow] = useState(false);

  const load = async () => {
    try {
      const [m, ba, adv, dcs, appts, st, tp, oo, cs] = await Promise.all([
        api.get("/metrics/overview"),
        api.get("/metrics/by-advisor"),
        api.get("/advisors"),
        api.get("/declines", { params: { advisor_id: advisorFilter, status: statusFilter, window: pipelineWindow } }),
        api.get("/appointments"),
        api.get("/settings"),
        api.get("/metrics/today"),
        api.get("/opt-outs/stats").catch(() => ({ data: { total: 0, recent: [] } })),
        api.get("/consent/stats").catch(() => ({ data: { no_consent: 0, consented: 0, total_unique_customers: 0 } })),
      ]);
      setMetrics(m.data);
      setByAdvisor(ba.data);
      setAdvisors(adv.data);
      setDeclines(dcs.data);
      const map = {};
      appts.data.forEach((a) => (map[a.decline_id] = a.scheduled_for));
      setApptByDecline(map);
      setDemoMode(st.data?.demo_mode !== false);
      setBodyShopEnabled(Boolean(st.data?.body_shop_enabled));
      setSettings(st.data);
      setTodayPulse(tp.data);
      setOptOuts(oo.data || { total: 0, recent: [] });
      setConsentStats(cs.data || { no_consent: 0, consented: 0, total_unique_customers: 0 });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => {
    load();
  }, [advisorFilter, statusFilter, pipelineWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  const advisorName = useMemo(() => {
    const m = {};
    advisors.forEach((a) => (m[a.id] = a.name));
    return m;
  }, [advisors]);

  const triggerImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/nightly");
      toast.success(`Imported ${data.imported} declined services from DMS`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const runCampaign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/campaign/run");
      toast.success(
        `Campaign sent — ${data.stage_1_sent} × ${settings?.stage_1_discount ?? 30}% offers, ${data.stage_2_sent} × ${settings?.stage_2_discount ?? 40}% offers`
      );
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const boostDemo = async () => {
    setBusy(true);
    try {
      const [conv, rec, bal] = await Promise.all([
        api.post("/demo/boost-conversions"),
        api.post("/demo/boost-recovered"),
        api.post("/demo/rebalance"),
      ]);
      toast.success(
        `Demo boosted — ${conv.data.scheduled} scheduled, ${conv.data.called_live} live calls, +$${rec.data.total_added.toLocaleString()} recovered · ${bal.data.orphans_reassigned} orphans reassigned, ${bal.data.appts_rescheduled} appts rescheduled`
      );
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendTestSms = async () => {
    if (!testForm.name.trim() || !testForm.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/demo/test-sms", {
        name: testForm.name.trim(),
        phone: testForm.phone.trim(),
        vehicle: testForm.vehicle.trim() || "Test Vehicle",
        service: testForm.service.trim() || "Recommended Service",
        discount: Number(testForm.discount),
      });
      toast.success(`Test message queued — check the SMS Log`);
      setTestOpen(false);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const personalizeForDealer = async () => {
    const name = personalizeName.trim();
    if (!name) {
      toast.error("Type the dealership name first");
      return;
    }
    if (!settings) return;
    setBusy(true);
    try {
      // PUT /api/settings requires the full SettingsModel — spread the current
      // settings and override just the dealership name.
      const payload = { ...settings, dealership_name: name };
      delete payload.id;
      delete payload._id;
      await api.put("/settings", payload);
      setPersonalizeGlow(true);
      toast.success(`App personalized for ${name}`);
      // Cinematic pause so the dealer sees the shimmer before we reload
      setTimeout(() => setPersonalizeGlow(false), 2200);
      await load();
      setPersonalizeName("");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not personalize");
    } finally {
      setBusy(false);
    }
  };

  const cycleBodyShopStatus = async (declineId, current) => {
    const nextStatus = BODY_SHOP_STATUS_META[current || "awaiting"].next;
    // Optimistic update
    setDeclines((prev) =>
      prev.map((d) => (d.id === declineId ? { ...d, body_shop_status: nextStatus } : d))
    );
    try {
      await api.patch(`/declines/${declineId}/body-shop-status`, { body_shop_status: nextStatus });
      toast.success(`Marked ${BODY_SHOP_STATUS_META[nextStatus].label}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      await load();
    }
  };

  // When demo_mode is on, the backend swaps calendar MTD/YTD for rolling
  // 30-day / 365-day windows. Mirror the label here so the UI matches the
  // data. Real dealers keep "This Month" / "Year-to-Date".
  const isDemoLabel = metrics?.demo_mode === true;
  const mtdLabelCaps = isDemoLabel ? "LAST 30 DAYS" : "THIS MONTH";
  const mtdLabelTitle = isDemoLabel ? "Declined · Last 30 Days" : "Declined This Month";
  const mtdHelper = isDemoLabel ? "Last 30 days · loaded from DMS" : "This month · loaded from DMS";

  return (
    <DashboardShell>
      {/* Personalize card — during a live pitch, James types the dealer's name
          and the entire app instantly rebrands (sidebar, founding dealer, One-Pager,
          Contract, Statement all now say "Priority Auto Group" in gold). */}
      <Card
        className={`mb-6 p-4 border-2 border-[hsl(var(--gold))]/60 bg-gradient-to-r from-[hsl(var(--gold))]/5 to-transparent no-print transition-all duration-500 ${
          personalizeGlow ? "ring-4 ring-[hsl(var(--gold))]/60 shadow-[0_0_40px_rgba(201,162,39,0.4)]" : ""
        }`}
        data-testid="personalize-card"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-3 md:min-w-[280px]">
            <div className="w-10 h-10 rounded-md bg-[hsl(var(--gold))] grid place-items-center shrink-0">
              <Wand2 className="w-5 h-5 text-neutral-900" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="font-mono-label text-[hsl(var(--gold))] text-[0.6rem]">One-Tap Rebrand</div>
              <div className="font-display font-bold text-sm truncate">
                Currently: <span className="text-[hsl(var(--gold))]">{settings?.dealership_name || "—"}</span>
              </div>
            </div>
          </div>
          <div className="flex-1 flex flex-col sm:flex-row gap-2">
            <Input
              value={personalizeName}
              onChange={(e) => setPersonalizeName(e.target.value)}
              placeholder="Type the dealership name (e.g. Priority Auto Group)"
              className="h-10 flex-1 border-[hsl(var(--gold))]/40 focus-visible:ring-[hsl(var(--gold))]"
              data-testid="personalize-input"
              onKeyDown={(e) => { if (e.key === "Enter") personalizeForDealer(); }}
            />
            <Button
              onClick={personalizeForDealer}
              disabled={busy || !personalizeName.trim()}
              className="h-10 bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold-light))] text-neutral-900 font-bold"
              data-testid="personalize-btn"
            >
              <Building2 className="w-4 h-4 mr-2" strokeWidth={2} />
              Personalize
            </Button>
          </div>
        </div>
      </Card>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-label text-muted-foreground">Dashboard</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tighter mt-1">
            Decline Recovery
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Track every declined service, follow-up text, and recovered appointment across your advisor team.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap no-print">
          {demoMode && (
            <Button
              variant="outline"
              onClick={() => setTourOpen(true)}
              data-testid="take-tour-btn"
              className="border-primary text-primary hover:bg-primary/10"
            >
              <PlayCircle className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Take the Tour
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setTestOpen(true)}
            data-testid="send-test-sms-btn"
            className="border-[hsl(var(--gold))] text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/10"
          >
            <Send className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Send Test SMS
          </Button>
          {demoMode && (
            <Button variant="outline" onClick={triggerImport} disabled={busy} data-testid="import-nightly-btn">
            <UploadCloud className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Nightly Import
          </Button>
          )}
          {demoMode && (
            <Button variant="outline" onClick={boostDemo} disabled={busy} data-testid="boost-demo-btn">
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Boost Demo
            </Button>
          )}
          <Button onClick={runCampaign} disabled={busy} data-testid="run-campaign-btn">
            <Zap className="w-4 h-4 mr-2" strokeWidth={2} />
            Run Campaign Cycle
          </Button>
        </div>
      </div>

      {/* LAST 7 DAYS live-pulse — one skinny row that answers "what's happening
          right now" without the dealer having to scan MTD/YTD. Wider than
          today so a slow single day doesn't undersell the number. */}
      <Card className="mb-4 p-0 overflow-hidden border-navy" data-testid="today-pulse-strip">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 bg-navy px-4 py-2.5">
          <div className="flex items-center gap-2 text-white md:shrink-0">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-mono-label tracking-widest text-[0.65rem]">LAST 7 DAYS · LIVE</span>
            {demoMode && (
              <span className="font-mono-label tracking-widest text-[0.55rem] text-blue-300 italic">example only</span>
            )}
          </div>
          <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-white text-[0.8rem]">
            <span
              title={`Rolling 7-day window · last 30 days total ${(metrics?.total_declines_mtd ?? metrics?.total_declines ?? 0).toLocaleString()}`}
            >
              <b className="font-display text-base tabular-nums">{todayPulse?.declines ?? 0}</b> {(todayPulse?.declines ?? 0) === 1 ? "decline" : "declines"}
              <span className="text-white/50 text-[0.72rem] ml-1">of {(metrics?.total_declines_mtd ?? metrics?.total_declines ?? 0).toLocaleString()} last 30 days</span>
            </span>
            <span className="text-white/40">·</span>
            <span
              title={`Rolling 7-day window · last 30 days total ${((metrics?.sms_stage_30_mtd ?? 0) + (metrics?.sms_stage_40_mtd ?? 0)).toLocaleString()}`}
            >
              <b className="font-display text-base tabular-nums text-blue-300">{todayPulse?.texts_fired ?? 0}</b> {(todayPulse?.texts_fired ?? 0) === 1 ? "text sent" : "texts sent"}
              <span className="text-white/50 text-[0.72rem] ml-1">of {((metrics?.sms_stage_30_mtd ?? 0) + (metrics?.sms_stage_40_mtd ?? 0)).toLocaleString()} last 30 days</span>
            </span>
            <span className="text-white/40">·</span>
            <span><b className="font-display text-base tabular-nums text-cyan-300">{todayPulse?.live_calls ?? 0}</b> live {(todayPulse?.live_calls ?? 0) === 1 ? "call" : "calls"}</span>
            <span className="text-white/40">·</span>
            <span><b className="font-display text-base tabular-nums text-white">{todayPulse?.appts_booked ?? 0}</b> {(todayPulse?.appts_booked ?? 0) === 1 ? "appt booked" : "appts booked"}</span>
            <span className="text-white/40">·</span>
            <span
              title={`Avg RO $${(todayPulse?.avg_ro ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} × ${todayPulse?.declines ?? 0} ${(todayPulse?.declines ?? 0) === 1 ? "decline" : "declines"} in the last 7 days`}
            >
              <b className="font-display text-base tabular-nums text-[hsl(var(--gold))]">${(todayPulse?.on_table ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b> open opportunity
            </span>
          </div>
        </div>
      </Card>

      {/* KPI grid — top strip stays dynamic; MTD/YTD pair cards hold the
          service-desk numbers James references in the pitch */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-2">
        <KpiCard
          label={mtdLabelTitle}
          value={metrics?.total_declines_mtd ?? metrics?.total_declines ?? "—"}
          sub={
            metrics ? (
              <div className="font-semibold text-navy dark:text-blue-200">
                → {(
                  (metrics.sms_stage_30_mtd ?? metrics.sms_stage_30 ?? 0) +
                  (metrics.sms_stage_40_mtd ?? metrics.sms_stage_40 ?? 0)
                ).toLocaleString()} offers sent (Offer 1 + Offer 2)
              </div>
            ) : (
              mtdHelper
            )
          }
          icon={Users}
          accent="bg-rose-500 text-white"
          testid="kpi-declined"
        />
        <KpiCard
          label="Offer 1 Sent"
          value={metrics?.sms_stage_30_mtd ?? metrics?.sms_stage_30 ?? "—"}
          sub={
            metrics && settings ? (
              <div className="text-muted-foreground">
                Day {settings.stage_1_days} · {settings.stage_1_discount}%
                <span className="text-muted-foreground/70"> · of {(metrics.total_declines_mtd ?? metrics.total_declines ?? 0).toLocaleString()} declines</span>
              </div>
            ) : settings ? (
              `Day ${settings.stage_1_days} · ${settings.stage_1_discount}%`
            ) : (
              "—"
            )
          }
          icon={MessageSquare}
          accent="bg-blue-600 text-white"
          testid="kpi-sms-30"
        />
        <KpiCard
          label="Offer 2 Sent"
          value={metrics?.sms_stage_40_mtd ?? metrics?.sms_stage_40 ?? "—"}
          sub={
            metrics && settings ? (
              <div className="text-muted-foreground">
                Day {settings.stage_2_days} · {settings.stage_2_discount}%
                <span className="text-muted-foreground/70"> · of {(metrics.total_declines_mtd ?? metrics.total_declines ?? 0).toLocaleString()} declines</span>
              </div>
            ) : settings ? (
              `Day ${settings.stage_2_days} · ${settings.stage_2_discount}%`
            ) : (
              "—"
            )
          }
          icon={MessageSquare}
          accent="bg-orange-500 text-white"
          testid="kpi-sms-40"
        />
        <KpiCard
          label="Opted Out"
          value={optOuts?.total ?? 0}
          sub={
            optOuts && optOuts.recent && optOuts.recent.length > 0 ? (
              <div className="text-muted-foreground" data-testid="kpi-optouts-recent">
                Latest: {optOuts.recent[0].phone} · {(optOuts.recent[0].keyword || "STOP")}
              </div>
            ) : (
              <div className="text-muted-foreground">Never send to these numbers again</div>
            )
          }
          icon={UserX}
          accent="bg-neutral-800 text-white"
          testid="kpi-optouts"
        />
        <KpiCard
          label="Needs Text Consent"
          value={consentStats?.no_consent ?? 0}
          sub={
            consentStats && consentStats.total_unique_customers > 0 ? (
              <div className="text-muted-foreground" data-testid="kpi-noconsent-sub">
                {consentStats.consented} of {consentStats.total_unique_customers} customers consented ·{" "}
                <b className="text-amber-700">fix intake to unlock the rest</b>
              </div>
            ) : (
              <div className="text-muted-foreground">
                Load declines with opt-in to enable texting
              </div>
            )
          }
          icon={ShieldAlert}
          accent="bg-amber-600 text-white"
          testid="kpi-noconsent"
        />
      </div>
      {/* Row 2 — funnel middle: Live Calls | Appointments Booked (sits under
          15% Offers Texted) | RO's Invoiced. 3-column matches the top row so
          each response metric lines up beneath the offer that drove it. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-2">
        {/* Live Calls — MTD/YTD + accountability roll of recent last names */}
        <Card className="border-border overflow-hidden p-0" data-testid="kpi-live-calls-pair">
          <div className="flex items-center justify-between px-4 py-2 bg-cyan-600 text-neutral-900">
            <div className="font-semibold uppercase tracking-wider text-[0.7rem]">Customer Calls</div>
            <Phone className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[0.6rem] font-mono-label text-muted-foreground">{mtdLabelCaps}</div>
                <div className="font-display text-3xl font-black tabular-nums text-navy" data-testid="live-calls-mtd">{metrics?.called_live_mtd ?? "—"}</div>
              </div>
              <div>
                <div className="text-[0.6rem] font-mono-label text-muted-foreground">YEAR-TO-DATE</div>
                <div className="font-display text-3xl font-black tabular-nums text-navy" data-testid="live-calls-ytd">{metrics?.called_live_ytd ?? "—"}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border text-[0.72rem] text-muted-foreground">
              Inbound calls from declined-service customers
            </div>
          </div>
        </Card>
        {/* Appointments Booked — sits directly under 15% Offers Texted */}
        <Card className="border-border overflow-hidden p-0" data-testid="kpi-scheduled-appt-pair">
          <div className="flex items-center justify-between px-4 py-2 bg-blue-600 text-neutral-900">
            <div className="font-semibold uppercase tracking-wider text-[0.7rem]">Booked</div>
            <CalendarCheck className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[0.6rem] font-mono-label text-muted-foreground">{mtdLabelCaps}</div>
                <div className="font-display text-3xl font-black tabular-nums text-navy" data-testid="appts-mtd">{metrics?.scheduled_mtd ?? "—"}</div>
              </div>
              <div>
                <div className="text-[0.6rem] font-mono-label text-muted-foreground">YEAR-TO-DATE</div>
                <div className="font-display text-3xl font-black tabular-nums text-navy" data-testid="appts-ytd">{metrics?.scheduled_ytd ?? "—"}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border text-[0.72rem] text-muted-foreground">
              Customers on the schedule{metrics?.total_declines_mtd > 0 && (<> — <b className="text-blue-700 dark:text-blue-300">{Math.round((metrics.scheduled_mtd ?? 0) / metrics.total_declines_mtd * 100)}% of declines booked</b></>)}
            </div>
          </div>
        </Card>
        {/* Invoiced ROs — MTD/YTD (was: Booked) */}
        <Card className="border-border overflow-hidden p-0" data-testid="kpi-invoiced-ros-pair">
          <div className="flex items-center justify-between px-4 py-2 bg-emerald-600 text-neutral-900">
            <div className="font-semibold uppercase tracking-wider text-[0.7rem]">Came Back &amp; Paid</div>
            <CalendarCheck className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[0.6rem] font-mono-label text-muted-foreground">{mtdLabelCaps}</div>
                <div className="font-display text-3xl font-black tabular-nums text-navy" data-testid="ros-mtd">{metrics?.came_back_paid_mtd ?? metrics?.closed_ro_count_month ?? "—"}</div>
              </div>
              <div>
                <div className="text-[0.6rem] font-mono-label text-muted-foreground">YEAR-TO-DATE</div>
                <div className="font-display text-3xl font-black tabular-nums text-navy" data-testid="ros-ytd">{metrics?.came_back_paid_ytd ?? metrics?.closed_ro_count_ytd ?? "—"}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border text-[0.7rem] text-muted-foreground leading-snug" data-testid="paid-split">
              {metrics ? (
                <>
                  <b className="text-navy dark:text-blue-200 tabular-nums">{metrics.booked_paid_mtd ?? 0}</b> booked through Decline Drive
                  <span className="mx-1">·</span>
                  <b className="text-navy dark:text-blue-200 tabular-nums">{metrics.walkin_paid_mtd ?? 0}</b> walk-in / called in
                </>
              ) : (
                "Any declined RO later invoiced — DD or walk-in"
              )}
            </div>
          </div>
        </Card>
      </div>
      {/* Row 3 — the money row, full width so it commands the eye */}
      <div className="mb-8">
        <Card className="border-border overflow-hidden p-0" data-testid="kpi-sales-recovery-pair">
          <div className="flex items-center justify-between px-4 py-2 bg-[hsl(var(--gold))] text-neutral-900">
            <div className="font-semibold uppercase tracking-wider text-[0.7rem]">Dollars Recovered · Closed RO $</div>
            <TrendingUp className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <div className="text-[0.65rem] font-mono-label text-muted-foreground">{mtdLabelCaps}</div>
                <div className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tabular-nums text-navy leading-none mt-1 break-words" data-testid="sales-mtd-dollars">${(metrics?.gross_recovered_month ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                <div className="text-[0.75rem] text-muted-foreground mt-1.5">from {metrics?.closed_ro_count_month ?? 0} closed RO's</div>
              </div>
              <div>
                <div className="text-[0.65rem] font-mono-label text-muted-foreground">YEAR-TO-DATE</div>
                <div className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tabular-nums text-navy leading-none mt-1 break-words" data-testid="sales-ytd-dollars">${(metrics?.gross_recovered_ytd ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                <div className="text-[0.75rem] text-muted-foreground mt-1.5">from {metrics?.closed_ro_count_ytd ?? 0} closed RO's</div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-border text-[0.72rem] text-muted-foreground">
              Actual dollars invoiced on recovered declines — no guesses
            </div>
          </div>
        </Card>
      </div>

      {/* By Advisor */}
      <Card className="p-0 mb-8 overflow-hidden border-border">
        <div className="flex items-center justify-between px-5 py-3 bg-navy text-white">
          <div>
            <div className="font-mono-label text-white/60 text-[0.6rem] tracking-widest">Team Performance</div>
            <h2 className="font-display text-lg font-bold tracking-tight mt-0.5 text-white">By Service Advisor</h2>
          </div>
          <TrendingUp className="w-5 h-5 text-[hsl(var(--gold))]" strokeWidth={2} />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Advisor</TableHead>
              <TableHead className="text-right">Offer 1 Sent</TableHead>
              <TableHead className="text-right">Offer 2 Sent</TableHead>
              <TableHead className="text-right">Customer Calls</TableHead>
              <TableHead className="text-right">Booked</TableHead>
              <TableHead className="text-right">Close %</TableHead>
              <TableHead className="text-right whitespace-nowrap">$ Last 30 Days</TableHead>
              <TableHead className="text-right whitespace-nowrap">$ YTD</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {byAdvisor.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">
                  <AlertTriangle className="inline w-4 h-4 mr-2" strokeWidth={1.75} />
                  No advisor data yet — run a Nightly Import to seed demo declines.
                </TableCell>
              </TableRow>
            )}
            {byAdvisor.length > 0 && (
              <TableRow className="bg-navy/5 dark:bg-navy/20 border-b-2 border-navy/30 font-semibold" data-testid="advisor-totals-row">
                <TableCell className="font-display font-black text-navy dark:text-blue-200">
                  TEAM TOTALS
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {byAdvisor.reduce((s, r) => s + (r.sms_stage_30 || 0), 0)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {byAdvisor.reduce((s, r) => s + (r.sms_stage_40 || 0), 0)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {byAdvisor.reduce((s, r) => s + (r.clicked_live || 0), 0)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {byAdvisor.reduce((s, r) => s + (r.booked ?? r.scheduled ?? 0), 0)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {(() => {
                    const totDecl = byAdvisor.reduce((s, r) => s + (r.total_declines || 0), 0);
                    const totPaid = byAdvisor.reduce((s, r) => s + (r.came_back_paid || 0), 0);
                    return totDecl > 0 ? `${Math.round((totPaid / totDecl) * 100)}%` : "0%";
                  })()}
                </TableCell>
                <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold whitespace-nowrap" data-testid="advisor-totals-sales-mtd">
                  ${byAdvisor.reduce((s, r) => s + (r.sales_mtd || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </TableCell>
                <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold whitespace-nowrap" data-testid="advisor-totals-sales-ytd">
                  ${byAdvisor.reduce((s, r) => s + (r.sales_ytd || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </TableCell>
              </TableRow>
            )}
            {byAdvisor.map((row) => {
              const adv = advisors.find((a) => a.id === row.advisor_id);
              const color = adv?.color || "#0f2b6b";
              const closePct = row.close_rate ?? (row.total_declines > 0
                ? Math.round(((row.came_back_paid || 0) / row.total_declines) * 1000) / 10
                : 0);
              return (
                <TableRow key={row.advisor_id} className="hover:bg-muted/40" data-testid={`advisor-row-${row.advisor_id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-background shadow-sm"
                        style={{ backgroundColor: color }}
                      />
                      <span style={{ color }}>{row.advisor_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.sms_stage_30}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.sms_stage_40}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.clicked_live}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.booked ?? row.scheduled ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    {closePct}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold whitespace-nowrap" data-testid={`advisor-sales-mtd-${row.advisor_id}`}>
                    ${Number(row.sales_mtd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold whitespace-nowrap font-semibold" data-testid={`advisor-sales-ytd-${row.advisor_id}`}>
                    ${Number(row.sales_ytd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Declines pipeline */}
      <Card className="p-0 overflow-hidden border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-5 border-b border-border">
          <div className="min-w-0">
            <div className="font-mono-label text-muted-foreground">Pipeline</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">Declined Services</h2>
            {/* Date-range chip row — dealer can slice pipeline by timeframe */}
            <div className="flex flex-wrap gap-1.5 mt-3 no-print" data-testid="pipeline-window-chips">
              {[
                { key: "all", label: "All time" },
                { key: "7d",  label: "Last 7d" },
                { key: "30d", label: "Last 30d" },
                { key: "mtd", label: "MTD" },
                { key: "ytd", label: "YTD" },
              ].map((w) => (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => setPipelineWindow(w.key)}
                  data-testid={`pipeline-window-${w.key}`}
                  className={`px-3 py-1 rounded-full text-[0.7rem] font-mono-label tracking-wider border transition-colors ${
                    pipelineWindow === w.key
                      ? "bg-navy text-white border-navy shadow-sm"
                      : "bg-background text-muted-foreground border-border hover:bg-muted"
                  }`}
                >
                  {w.label}
                </button>
              ))}
              <span className="px-2 py-1 text-[0.65rem] text-muted-foreground tabular-nums self-center" data-testid="pipeline-window-count">
                {declines.length} shown
              </span>
            </div>
          </div>
          <div className="flex gap-2 no-print">
            <Select value={advisorFilter} onValueChange={setAdvisorFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-advisor">
                <SelectValue placeholder="Advisor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All advisors</SelectItem>
                {advisors.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {/* Deduplicate on visible label so legacy aliases stage_30 / stage_40
                    don't create duplicate rows in the filter dropdown */}
                {Object.entries(STATUS_LABELS(settings))
                  .filter(([k, v], _i, arr) => arr.findIndex(([, v2]) => v2 === v) === arr.findIndex(([k2]) => k2 === k))
                  .map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="print-pipeline-btn"
            >
              <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Print
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Declined Services</TableHead>
              <TableHead>Advisor</TableHead>
              <TableHead>Declined</TableHead>
              <TableHead>
                <div className="flex items-center gap-1.5">
                  <span>Status</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-navy transition-colors"
                        aria-label="What do these status colors mean?"
                        data-testid="status-legend-trigger"
                      >
                        <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-[340px] p-4" data-testid="status-legend-popover">
                      <div className="font-display font-bold text-sm text-navy mb-2">
                        What each status means
                      </div>
                      <p className="text-[0.7rem] text-muted-foreground leading-snug mb-3">
                        These are the six stages of the decline-recovery funnel.
                        <b> Only grey "Declined" is untouched</b> — every colored pill means the customer already got a coupon.
                      </p>
                      <ul className="space-y-2 text-[0.75rem]">
                        <li className="flex items-start gap-2">
                          <Badge className="bg-stone-500 text-white shrink-0 mt-0.5">Declined</Badge>
                          <span className="text-muted-foreground leading-snug">Customer said "no" at the counter. <b>Nothing sent yet</b> — waiting for the campaign to fire.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Badge className="bg-blue-500 text-white shrink-0 mt-0.5 whitespace-nowrap">1st Offer · {settings?.stage_1_discount ?? 30}%</Badge>
                          <span className="text-muted-foreground leading-snug"><b>1st SMS fired</b> with the smaller coupon. Customer has it — hasn't replied yet.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Badge className="bg-orange-500 text-white shrink-0 mt-0.5 whitespace-nowrap">2nd Offer · {settings?.stage_2_discount ?? 40}%</Badge>
                          <span className="text-muted-foreground leading-snug"><b>2nd (sweeter) SMS fired</b> ~7-10 days later. Hot lead — often the one that converts.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Badge className="bg-emerald-600 text-white shrink-0 mt-0.5">Scheduled</Badge>
                          <span className="text-muted-foreground leading-snug">Customer <b>clicked the SMS link and booked</b>. 💰 Win.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Badge className="bg-cyan-600 text-white shrink-0 mt-0.5">Called Live</Badge>
                          <span className="text-muted-foreground leading-snug">Customer <b>called the shop directly</b> instead of clicking. 💰 Win.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Badge className="bg-red-500 text-white shrink-0 mt-0.5">No Response</Badge>
                          <span className="text-muted-foreground leading-snug">Both offers sent, no bite. <b>Not dead</b> — still eligible for the 60/90-day Comeback Protocol.</span>
                        </li>
                      </ul>
                    </PopoverContent>
                  </Popover>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {declines.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">
                  No declined services match these filters.
                </TableCell>
              </TableRow>
            )}
            {declines.map((d) => {
              const bodyShop = bodyShopEnabled && isBodyShop(d.service_items);
              const bsMeta = BODY_SHOP_STATUS_META[d.body_shop_status || "awaiting"];
              return (
                <TableRow
                  key={d.id}
                  className={`hover:bg-muted/40 ${bodyShop ? "bg-rose-50/40 dark:bg-rose-950/10" : ""}`}
                  data-testid={`decline-row-${d.id}`}
                >
                  <TableCell>
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <span>{d.customer_name}</span>
                      {bodyShop && (
                        <Badge
                          className="bg-rose-600 text-white text-[0.55rem] px-1.5 py-0"
                          data-testid={`body-shop-badge-${d.id}`}
                        >
                          BODY SHOP
                        </Badge>
                      )}
                      {bodyShop && (
                        <button
                          type="button"
                          onClick={() => cycleBodyShopStatus(d.id, d.body_shop_status)}
                          className={`text-[0.55rem] px-1.5 py-0.5 rounded font-medium transition-colors ${bsMeta.cls}`}
                          title="Click to cycle: Awaiting → Showed Up → No Show"
                          data-testid={`body-shop-status-${d.id}`}
                        >
                          {bsMeta.label}
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{d.customer_phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">{d.vehicle}</TableCell>
                  <TableCell className="text-sm max-w-[260px]">
                    {d.service_items.map((s) => s.name).join(", ")}
                  </TableCell>
                  <TableCell className="text-sm">{advisorName[d.advisor_id] || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(d.decline_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_STYLES[d.status] || ""}>
                      {STATUS_LABELS(settings)[d.status] || d.status}
                    </Badge>
                    {d.status === "scheduled" && apptByDecline[d.id] && (
                      <div className="text-xs text-muted-foreground mt-1 whitespace-nowrap" data-testid={`scheduled-for-${d.id}`}>
                        {new Date(apptByDecline[d.id]).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <ScrollToTopButton testid="dashboard-scroll-top" />

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="test-sms-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Send a Test SMS</DialogTitle>
            <DialogDescription>
              Fire an offer to any phone number to see the customer flow live. Uses the same wording your
              real customers get — messages appear in the SMS Log (and hit the phone once Twilio keys are added).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tname" className="font-mono-label">Customer Name</Label>
              <Input
                id="tname"
                value={testForm.name}
                onChange={(e) => setTestForm({ ...testForm, name: e.target.value })}
                placeholder="Alicia Bennett"
                className="mt-1.5"
                data-testid="test-name-input"
              />
            </div>
            <div>
              <Label htmlFor="tphone" className="font-mono-label">Phone Number</Label>
              <Input
                id="tphone"
                value={testForm.phone}
                onChange={(e) => setTestForm({ ...testForm, phone: e.target.value })}
                placeholder="+15551234567"
                className="mt-1.5"
                data-testid="test-phone-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="tveh" className="font-mono-label">Vehicle</Label>
                <Input
                  id="tveh"
                  value={testForm.vehicle}
                  onChange={(e) => setTestForm({ ...testForm, vehicle: e.target.value })}
                  className="mt-1.5"
                  data-testid="test-vehicle-input"
                />
              </div>
              <div>
                <Label htmlFor="tdisc" className="font-mono-label">Discount</Label>
                <Select value={testForm.discount} onValueChange={(v) => setTestForm({ ...testForm, discount: v })}>
                  <SelectTrigger id="tdisc" className="mt-1.5" data-testid="test-discount-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">{settings?.stage_1_discount ?? 30}% (Day {settings?.stage_1_days ?? 7} offer)</SelectItem>
                    <SelectItem value="40">{settings?.stage_2_discount ?? 40}% (Day {settings?.stage_2_days ?? 15} offer)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="tsvc" className="font-mono-label">Recommended Service</Label>
              <Input
                id="tsvc"
                value={testForm.service}
                onChange={(e) => setTestForm({ ...testForm, service: e.target.value })}
                className="mt-1.5"
                data-testid="test-service-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)} data-testid="test-cancel-btn">
              Cancel
            </Button>
            <Button onClick={sendTestSms} disabled={busy} data-testid="test-send-btn">
              <Send className="w-4 h-4 mr-2" strokeWidth={2} />
              Fire the offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
